import React, { useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import { FieldsContainer, PhoneNumberVerificationContainer, ResendCode, StyledBlueSpan } from "./PhoneNumberVerification.styles";
import { Button, InfoMessage, TitleSubtitle } from "../..";
import { VerificationCodeInput } from "../../VerificationCodeInput/VerificationCodeInput";
import { ButtonContainer, ButtonIcon, ButtonLabel, CTABackAndForthContainer } from "../../../pages/Register/Register.styles";
import { useRegisterContext } from "../../../contexts/RegisterContext";
import { registerProfile, sendConfirmPhoneNumberSMS } from "../../../services/AuthenticationService";
import { SendConfirmPhoneNumberSMSInterface } from "../../../models/Register";
import { useAuthContext } from "src/contexts/AuthContext";
import { extractErrorMessageFromProblemDetails } from "src/utils/string-utils";

interface PhoneNumberVerificationProps extends WithRouterProps {
    onNextButtonClick: Function;
    onPrevButtonClick: Function;
}

const PhoneNumberVerification: React.FC<PhoneNumberVerificationProps> = ({
    onNextButtonClick,
    onPrevButtonClick,
}) => {
    const inputLength = 6;
    const [verificationCodeHasError, setVerificationCodeHasError] = useState<boolean>(false);
    const { registerInfo, setRegisterInfo } = useRegisterContext();
    const { setUser } = useAuthContext();
    const [loading, setLoading] = useState<boolean>(false);

    const [registerProfileError, setRegisterProfileError] = useState<boolean>(false);
    const [showInfoMessage, setShowInfoMessage] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [infoMessage, setInfoMessage] = useState("");

    const handleRegisterProfile = () => {
        setLoading(true);
        registerProfile(registerInfo).then(async (response) => {
            if (response.data) {
                setUser(response.data.user);
                onNextButtonClick();
            } else if (response.error) {
                setRegisterProfileError(true);
                if (typeof response.error === "string") {
                    setErrorMessage(response.error)
                } else {
                    setErrorMessage(extractErrorMessageFromProblemDetails(response.error));
                }
            } else {
                setRegisterProfileError(true);
                setErrorMessage("Unable to complete registration. Please contact support at audience@thatstheticket.com.au");
            }
            setLoading(false);
        });
    }

    const handleResendVerificationcode = () => {
        const confirmPhoneNumberData = {
            email: registerInfo.user.email,
            phoneNumber: registerInfo.user.phoneNumber
        } as SendConfirmPhoneNumberSMSInterface

        sendConfirmPhoneNumberSMS(confirmPhoneNumberData).then((res) => {
            if (res.status == 400) {
                setRegisterProfileError(true);
                setErrorMessage(res.errors?.message ? res.errors?.message : "An error occured");
            } else if (res.status == 200) {
                setShowInfoMessage(true);
                setInfoMessage("We have resent your phone verification code. If you don't receive it shortly, ensure your phone number is correct, and try again.")
            }
        });
    }

    const isContinueButtonDisabled = registerInfo.phoneNumberVerificationCode.length < inputLength || verificationCodeHasError ? true : false;

    return (
        <PhoneNumberVerificationContainer>
            <TitleSubtitle
                size="sm"
                title="Verify Your Number"
                subTitle="Enter the code we just sent you via text below" />
            <FieldsContainer>
                <VerificationCodeInput
                    errorFromOutside={true}
                    inputLength={6}
                    getConcatenatedValues={(value: string) => {
                        setRegisterInfo(prevRegisterInfo => ({
                            ...prevRegisterInfo,
                            phoneNumberVerificationCode: value
                        }))
                        setShowInfoMessage(false);
                    }}
                    hasError={(value: boolean) => { setVerificationCodeHasError(value) }} />
            </FieldsContainer>
            <ResendCode>Didn’t get it? <StyledBlueSpan onClick={handleResendVerificationcode}>Resend code</StyledBlueSpan></ResendCode>
            {registerProfileError && (
                <InfoMessage
                    data-cy="register-error"
                    variant="error"
                    hasBackground={true}
                    message={errorMessage}
                />
            )}
            {showInfoMessage && (
                <InfoMessage
                    data-cy="register-warning"
                    variant="info"
                    hasBackground={true}
                    message={infoMessage}
                />
            )}
            <CTABackAndForthContainer>
                <Button
                    size="md"
                    variant="tertiary"
                    onClick={() => onPrevButtonClick()}
                    data-cy="button-booking-next"
                    sx={{ padding: 0 }}
                >
                    <ButtonContainer>
                        <ButtonIcon icon="ArrowLeft" />
                        <ButtonLabel>Back</ButtonLabel>
                    </ButtonContainer>
                </Button>
                <Button
                    data-cy="register-phone-number-verification-code-submit"
                    onClick={handleRegisterProfile}
                    disabled={isContinueButtonDisabled}
                    loading={loading}
                > Verify Number </Button>
            </CTABackAndForthContainer>
        </PhoneNumberVerificationContainer>
    );
};

PhoneNumberVerification.displayName = "PhoneNumberVerification";

const ComponentWithRouter = withRouter(PhoneNumberVerification)

export { ComponentWithRouter as PhoneNumberVerification };
